import * as Actions from 'actions'
import cx from 'classnames'
import { Box } from '@material-ui/core'
import { TranslationContext } from 'contexts/TranslationContext'
import { useAction, useModalDialog, useOnClickWithoutPropagation } from 'hooks'
import { isEqual, noop, some } from 'lodash'
import PropTypes from 'prop-types'
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useDrag } from 'react-dnd'
import { getEmptyImage } from 'react-dnd-html5-backend'
import { useSelector } from 'react-redux'
import { SelectableGroup } from 'react-selectable-fast-control'
import { useHotkeys } from 'react-hotkeys-hook'
import * as PT from '~/PropTypes'
import { CircularProgress } from '~/components/base/CircularProgress/CircularProgress'
import {
  DRAGNDROP_TYPE,
  MENU_ITEMS,
  SOURCE_FILE_TYPES,
  HOTKEYS,
  FILE_STATUS
} from '~/enums'
import { useRefWithSetter } from '~/hooks/utils'
import * as Assets from '~/models/Asset'
import { Folder } from '~/models/Folder'
import * as Selectors from '~/selectors'
import styles from './BaseAssetsList.module.scss'
import './BaseAssetsList.scss'
import { Orientation } from '~/types/userInterface'
import { TransformUIDropContainer } from '~/components/TransformUIDropContainer'
import { DROP_CONTAINER, UI_ELEMENT } from '~/config/constants/ui'
import { useTransformUI } from '~/hooks/useTransformUI'
import { selectSplitSize, selectActiveInteractionUIElement } from '~/selectors/useInterface'
import {
  VIRTUAL_SCROLL_CONTAINER_CLASS,
  MEDIA_ELEMENTS_SIZE,
  LEFT_MENU_WIDTH
} from '~/constant'
import { setActiveDragElement, setPendingSelectedAssetId, setReferenceFileChoice, showDeleMediaFilesFormAction } from '~/actions/mainView'
import { isVideo } from '~/Util/assets'
import { getIncompatibleFileExtensions } from '~/helpers/incompatibleFileExtensions'
import { selectPendingSelectedAssetId, selectShowReferenceFileChoice } from '~/selectors/mainView'
import { activeHotkeyProfileSelector } from '~/selectors/user-profile-selector'
import { MediaUsedOnDeletionError } from '~/errors/MediaUsedOnDeletionError'
import { ConfirmationDialog } from '~/components/base/ConfirmationDialog/ConfirmationDialog'
import { setActiveInteractionUIElement } from '~/actions/userInterface'
import { VirtualList } from './components/vitrual-list/VirtualList'
import { SourceMediaListHeader } from './components/header/SourceMediaListHeader'
import { useResizeBaseAssetsList } from './hooks/useResizeBaseAssetsList'
import { clearAssetsSelection } from '~/actions/layer'
import { filterAssetsByParams } from '~/helpers/assets/filterAssetsByParams'

const empty = () => {}

export const BaseAssetsListContext = React.createContext({
  /** @type {UploadableAsset[]} */
  selectedItems: [],
  setSelectedItems: () => {},
  folders: [],
  deletionDialog: { open: noop, cancel: noop, confirm: noop, active: false },
  hotkeyDeletionDialog: { open: noop, cancel: noop, confirm: noop, active: false },
  foldersDialog: { open: noop, cancel: noop, confirm: noop, active: false },
  handleFolderSelect: noop,
  onMoveMediaItemsToFolder: noop,
  selectedFolder: null,
  onOpenContextMenu: noop,
  onCloseContextMenu: noop,
  isOpenContextMenu: false,
  menuPositionCoords: {},
  clickedMediaElemId: null,
  setSelectedFolder: noop,
  isOpenDialog: false,
})

function BaseAssetsList(props) {
  const {
    className,
    /** @type {Asset[]} */
    items,
    loading,
    header,
    ItemComponent,
    before,
    foldersNavBar,
    placeholder,
    after,
    float,
    onScroll,
  } = props

  const {
    selectedItems,
    setSelectedItems,
    hotkeyDeletionDialog,
  } = useContext(BaseAssetsListContext)
  const refFiles = useSelector(selectShowReferenceFileChoice)
  const pendingSelectedAssetId = useSelector(selectPendingSelectedAssetId)
  const setPendingSelection = useAction(setPendingSelectedAssetId)
  const setUIElementBounds = useAction(Actions.useInterface.setUIElementBounds)

  const [ isSelectableGroupReady, setIsSelectableGroupReady ] = useState(false)
  const [ selectableGroupRef, setSelectableGroupRefState ] = useRefWithSetter()
  const setSelectableGroupRef = useCallback(ref => {
    setSelectableGroupRefState(ref)
    setIsSelectableGroupReady(!!ref)
  }, [ setSelectableGroupRefState ])
  const onMoveMediaItemsToFolder = useAction(Actions.sourceFiles.moveMediaItemsToFolder)
  const onSetReferenceFileChoice = useAction(setReferenceFileChoice)
  const onSetActiveInteractionUIElement = useAction(setActiveInteractionUIElement)
  const clearTimelineAssetsSelection = useAction(clearAssetsSelection)
  const { menuItem } = useSelector(state => state.mainView)
  const { media: mediaColumnSize } = useSelector(selectSplitSize)
  const {
    assetsListSize,
    assetListHeaderSize,
    assetsListRef,
    assetListHeaderRef,
  } = useResizeBaseAssetsList()

  const handleSelectionClear = useCallback(noClear => {
    setSelectedItems(prev => {
      if (noClear) {
        return prev
      }
      return []
    })
  }, [ setSelectedItems ])
  const [ , drag ] = useDraggable(
    {
      onMoveMediaItemsToFolder,
      handleSelectionClear,
    },
    selectedItems
  )

  const isItemsDraggable = selectedItems.length <= 1

  const handleItemMouseDown = useCallback(__CFG__.SOURCE_FILES_MANAGEMENT.SELECTABLE
    ? (assetId, fn) => {
      if (!selectedItems.map(item => item.id).includes(assetId)) {
        selectableGroupRef.current.clearSelection()
        fn()
      }
    }
    : empty, [ selectedItems ])

  const handleItemClick = useCallback(__CFG__.SOURCE_FILES_MANAGEMENT.SELECTABLE
    ? (assetId, fn) => {
      const item = selectedItems.find(item => item.id === assetId)
      if (item) {
        selectableGroupRef.current.clearSelection()
        fn()
        // called after handleSelectionClear
        setTimeout(() => {
          onSetReferenceFileChoice([ item ])
        })
      }
    }
    : empty, [ selectedItems, onSetReferenceFileChoice ])

  const onSelectionFinish = useCallback(selectedElems => {
    if (selectedElems.length === 0) return
    const items = selectedElems.map(item => ({
      id: item.props.asset.id,
      name: item.props.asset.name,
      type: getDraggableType(item.props.asset),
      hasError: item.props.asset.hasError,
      uploading: item.props.asset.uploading,
      originalUrl: item.props.asset.originalUrl || '',
      path: item.props.asset.path || '',
      asset: item.props.asset,
      filetype: item.props.asset.filetype,
      status: item.props.asset.status,
    }))
    setSelectedItems(() => items)
    onSetReferenceFileChoice(items)
  }, [ onSetReferenceFileChoice, setSelectedItems ])

  useEffect(() => {
    if (!isEqual(selectedItems, refFiles)) {
      onSetReferenceFileChoice(selectedItems)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ selectedItems, refFiles ])

  useEffect(() => {
    const assetsListContainer = assetsListRef.current
    const setInteractionUIElement = () => {
      onSetActiveInteractionUIElement(UI_ELEMENT.SOURCE_LIST)
      clearTimelineAssetsSelection()
    }
    if (assetsListContainer) {
      assetsListContainer.addEventListener('mouseup', setInteractionUIElement)
    }
    return () => {
      if (assetsListContainer) {
        assetsListContainer.removeEventListener('mouseup', setInteractionUIElement)
      }
    }
  }, [ assetsListRef, onSetActiveInteractionUIElement, clearTimelineAssetsSelection ])

  useEffect(() => {
    // need to make selection after importMediafileFromUrl
    if (!pendingSelectedAssetId || !selectableGroupRef.current) return
    const group = selectableGroupRef.current
    if (items?.length && !items.some(item => item.id === pendingSelectedAssetId)) {
      setPendingSelection(null)
      return
    }
    const target = [ ...group.registry ]
      .find(item => item.props?.asset?.id === pendingSelectedAssetId)
    if (!target) return
    setTimeout(() => {
      group.clearSelection()
      target.setState({ isSelected: true })
      group.selectedItems.add(target)
      group.toggleSelectionMode()
      group.props.onSelectionFinish([ ...group.selectedItems ])
      setPendingSelection(null)
    }, 0)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ pendingSelectedAssetId, items, isSelectableGroupReady, setPendingSelection ])

  const { t } = useContext(TranslationContext)
  const { drag: setDrag, isDragging } = useTransformUI({ type: DRAGNDROP_TYPE.SOURCE_LIST_HEADER })

  const isMedia = [ MENU_ITEMS.MEDIA, MENU_ITEMS.PROJECT_MEDIA ].includes(menuItem)
  const { width, height: itemSize } = MEDIA_ELEMENTS_SIZE[menuItem]
  const countRowSourceAssets = Math.floor((mediaColumnSize - LEFT_MENU_WIDTH) / width)
  const rowCount = Math.ceil((items.length + 1) / countRowSourceAssets)

  const itemData = useMemo(() => ({
    items,
    ItemComponent,
    countRowSourceAssets,
    handleItemMouseDown,
    handleItemClick,
    setUIElementBounds,
    mediaColumnSize,
    menuItem,
    isItemsDraggable,
    before,
    after,
    rowCount,
  }), [
    items, ItemComponent, countRowSourceAssets, handleItemMouseDown, handleItemClick,
    setUIElementBounds, mediaColumnSize, menuItem, isItemsDraggable, before, after, rowCount,
  ])
  const handleScroll = useCallback(({
    scrollOffset,
    scrollDirection,
    scrollUpdateWasRequested,
  }) => {
    if (__CFG__.SOURCE_FILES_MANAGEMENT.LOAD_MEDIA_ONSCROLL && onScroll) {
      onScroll({
        scrollOffset,
        scrollDirection,
        scrollUpdateWasRequested,
      })
    }
  }, [ onScroll ])

  return (
    <div
      className={cx('assets-list', className)}
      ref={assetsListRef}
      style={{ height: '100%' }}
    >
      {isDragging && (
        <TransformUIDropContainer
          orientation={Orientation.Horizontal}
          width="100%"
          height="10px"
          dropContainer={DROP_CONTAINER.HEADER_MENU}
        />
      )}
      <SourceMediaListHeader
        isDragging={isDragging}
        setDrag={setDrag}
        header={header}
        menuItem={menuItem}
        ref={assetListHeaderRef}
      />
      <div>{foldersNavBar}</div>
      <Choose>
        <When condition={loading}>
          <Box width="100%" height="100%">
            <CircularProgress
              size={100}
              endless
              percents={80}
              text={`${t('MEDIA_PROGRESS_TEXT_LOADING')}..`}
              transparent
            />
          </Box>
        </When>
        <When condition={!items?.length && !loading}>
          <Box
            position="relative"
            marginTop="3px"
            className={cx(`${styles.selectableGroup}
              assets-list__selectable-group`)}
          >
            {placeholder}
          </Box>
        </When>
        <Otherwise>
          <SelectableGroup
            style={{ height: '100%', minHeight: '100%' }}
            ref={setSelectableGroupRef}
            className={cx(`${styles.selectableGroup}
              assets-list__selectable-group`)}
            disabled={
              !__CFG__.SOURCE_FILES_MANAGEMENT.SELECTABLE
              || !isMedia
            }
            selectboxClassName={styles.selectBox}
            scrollContainer={`.${VIRTUAL_SCROLL_CONTAINER_CLASS}`}
            dragContainer={`.${VIRTUAL_SCROLL_CONTAINER_CLASS}`}
            selectOnClick
            deselectOnEsc={false}
            resetOnStart
            allowCtrlClick
            allowShiftClick
            ignoreList={[ '.source-asset div', '.source-asset.not-selectable' ]}
            onSelectionClear={handleSelectionClear}
            onSelectionFinish={onSelectionFinish}
          >
            <VirtualList
              height={assetsListSize.height - assetListHeaderSize.height}
              rowCount={rowCount}
              itemSize={itemSize}
              itemData={itemData}
              onScroll={handleScroll}
              drag={drag}
            />
          </SelectableGroup>
          <If condition={hotkeyDeletionDialog.active}>
            <ConfirmationDialog
              onClick={e => e.stopPropagation()}
              onCancel={hotkeyDeletionDialog.cancel}
              onConfirm={hotkeyDeletionDialog.confirm}
              confirmBtnText={t('MEDIA_CONFIRM_BTN_DELETE')}
              closeBtnText={t('MEDIA_CANCEL_BTN_DELETE')}
              description={t('MEDIA_CONFIRM_DESC_ARE_YOU_SURE_TO_REMOVE_SELECTED')}
            />
          </If>
        </Otherwise>
      </Choose>
      <div>{float}</div>
    </div>
  )
}

BaseAssetsList.propTypes = {
  items: PropTypes.arrayOf(PT.MediaItem),
  loading: PropTypes.bool,
  header: PropTypes.element.isRequired,
  ItemComponent: PropTypes.elementType.isRequired,
  before: PropTypes.element,
  foldersNavBar: PropTypes.element,
  placeholder: PropTypes.element,
  after: PropTypes.element,
  float: PropTypes.element,
  onScroll: PropTypes.func,
  classes: PropTypes.shape({ root: PropTypes.string, list: PropTypes.string }),
}

BaseAssetsList.defaultProps = {
  items: [],
  loading: false,
  before: null,
  foldersNavBar: null,
  placeholder: null,
  after: null,
  float: null,
  onScroll: () => {},
  classes: { root: '', list: '' },
}

function getDraggableType(asset) {
  switch (asset.constructor) {
    case Assets.VideoAsset:
      return DRAGNDROP_TYPE.MEDIA_ITEM
    case Assets.ImageAsset:
      return DRAGNDROP_TYPE.IMAGE_ITEM
    case Assets.TextAsset:
      return DRAGNDROP_TYPE.TEXT_ITEM
    case Assets.AudioAsset:
      return DRAGNDROP_TYPE.AUDIO_ITEM
    case Assets.TransitionAsset:
      return DRAGNDROP_TYPE.TRANSITION_ITEM
    case Folder:
      return DRAGNDROP_TYPE.FOLDER
    default:
      throw new Error(`Unsupported asset type: ${asset.constructor.name}`)
  }
}

function useDraggable(options, ...deps) {
  const setDndDropTarget = useAction(Actions.mainView.setDndDropTarget)
  const onSetActiveDragElement = useAction(setActiveDragElement)
  const { handleSelectionClear, onDenied } = options
  const [ selectedItems ] = deps

  const item = {
    type: DRAGNDROP_TYPE.GROUP,
    selectedItems,
    handleSelectionClear,
    incompatibleFileExtensions: selectedItems.reduce((res, item) => {
      const result = { ...res }
      if (isVideo(item)) {
        result[item.filetype] = getIncompatibleFileExtensions(item.filetype)
      }
      return result
    }, {}),
  }
  const [ collected, drag, preview ] = useDrag(
    {
      item,
      begin: () => {
        setDndDropTarget('root')
        onSetActiveDragElement(DRAGNDROP_TYPE.GROUP)
      },
      end() {
        onSetActiveDragElement(null)
      },
      canDrag() {
        const readyDragStatus = [ FILE_STATUS.READY, FILE_STATUS.PREPARE_THUMBNAILS ]
        if (
          (some(selectedItems, item => item.uploading)
          && !some(selectedItems, item => item.hasError))
          || some(selectedItems, item => !readyDragStatus.includes(item.status))
        ) {
          onDenied()
          return false
        }
        return selectedItems.length > 1
      },
    },
    deps
  )

  React.useEffect(() => {
    preview(getEmptyImage())
  }, [ preview ])
  return [ collected, drag ]
}

// ---

function AssetsListContainer(props) {
  const { type, sort, ...rest } = props
  const isProjectMedia = type === SOURCE_FILE_TYPES.PROJECT_MEDIA
  const prevParams = useRef(null)
  const [ errorTitle, setErrorTitle ] = useState('')
  const [ errorMessage, setErrorMessage ] = useState('')
  const { items: groupItems, params } = useSelector(
    state => Selectors.getSourceFilesGroup(state, type)
  )

  const projectMediaItems = useMemo(() => isProjectMedia ? filterAssetsByParams(
    groupItems, params, prevParams
  ) : [], [ groupItems, params, prevParams, isProjectMedia ])

  const items = isProjectMedia ? projectMediaItems : groupItems
  const loading = useSelector(state => Selectors.getIsSourceFilesLoading(state, type))
  const sourceFilesParams = useSelector(state => Selectors.selectSourceFilesParams(state, type))
  const activeHotkeyProfile = useSelector(activeHotkeyProfileSelector)
  const activeInteractionUIElement = useSelector(selectActiveInteractionUIElement)
  const selectedAssets = useSelector(Selectors.getSelectedAssets)
  const [ sorted, setSorted ] = useState(items)
  const [ selectedItems, setSelectedItems ] = useState([])
  const [ selectedFolder, setSelectedFolder ] = useState(null)
  const [ isOpenContextMenu, setOpenContextMenu ] = useState(false)
  const [ clickedMediaElemId, setClickedMediaElemId ] = useState(null)
  const menuPositionCoords = useRef(null)

  const selectedItemsByType = useMemo(() => {
    setOpenContextMenu(false)
    return selectedItems.reduce((result, item) => {
      if (item.type === DRAGNDROP_TYPE.FOLDER) {
        result.folders.push(item.id)
      } else if (item.type !== DRAGNDROP_TYPE.FOLDER) {
        result.files.push(item.id)
      }
      return result
    }, { folders: [], files: [] })
  }, [ selectedItems ])

  const onDeleteMediaFiles = useAction(showDeleMediaFilesFormAction, { visibility: true })
  const onMoveMediaAction = useAction(Actions.sourceFiles.moveMediaItemsToFolder)
  const onMoveMediaItemsToFolder = (isFolder, id) => {
    onMoveMediaAction({
      folders: isFolder ? [ id ] : selectedItemsByType.folders,
      files: isFolder ? [] : selectedItemsByType.files,
      targetFolder: selectedFolder,
      type: SOURCE_FILE_TYPES.MEDIA,
    })
  }
  const deleteFolder = useAction(Actions.sourceFiles.requestDeleteFolder)
  const deleteFolderForce = useCallback(folderId => {
    deleteFolder(folderId, SOURCE_FILE_TYPES.MEDIA, true)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const deletionDialog = useModalDialog({ onConfirm: onDeleteMediaFiles, onCancel })
  const hotkeyDeletionDialog = useModalDialog({ onConfirm: deleteWithHotkey, onCancel })
  const foldersDialog = useModalDialog({ onConfirm: onMoveMediaItemsToFolder, onCancel })
  const singleDeletion = useModalDialog({ onConfirm: deleteSingleMediaItem, onCancel })
  const forceDeletionFolderDialog = useModalDialog({ onConfirm: deleteFolderForce, onCancel })

  const isOpenDialog = deletionDialog.active
  || hotkeyDeletionDialog.active
  || foldersDialog.active
  || singleDeletion.active
  || forceDeletionFolderDialog.active

  const loadingShow = loading && sourceFilesParams.start === 0

  const onOpenContextMenu = useCallback((e, id) => {
    e.stopPropagation()
    menuPositionCoords.current = { x: e.clientX, y: e.clientY }
    setClickedMediaElemId(id)
    setOpenContextMenu(true)
  }, [])

  const onCloseContextMenu = useOnClickWithoutPropagation(() => {
    setOpenContextMenu(false)
    menuPositionCoords.current = null
  })

  const handleFolderSelect = useCallback(id => {
    setSelectedFolder(id)
  }, [])

  function onCancel() {
    setOpenContextMenu(false)
    setClickedMediaElemId(null)
  }

  function deleteWithHotkey() {
    if (!selectedAssets?.length) {
      onDeleteMediaFiles()
    }
  }

  function onFolderDeletionError(e) {
    if (e instanceof MediaUsedOnDeletionError) {
      const { title, type } = e
      setErrorTitle(title)
      setErrorMessage(type)
      forceDeletionFolderDialog.open()
    }
  }

  async function deleteSingleMediaItem({ isFolder, id }) {
    if (isFolder) {
      try {
        await deleteFolder(id, SOURCE_FILE_TYPES.MEDIA)
      } catch (e) {
        onFolderDeletionError(e)
      }
    } else {
      onDeleteMediaFiles()
    }
  }

  const onHotkeyAction = useCallback(() => {
    if (selectedItems.length
      && activeInteractionUIElement === UI_ELEMENT.SOURCE_LIST) {
      hotkeyDeletionDialog.open()
    }
  }, [ hotkeyDeletionDialog, selectedItems, activeInteractionUIElement ])

  useHotkeys(activeHotkeyProfile.hotkeys[HOTKEYS.DELETE], onHotkeyAction)
  useEffect(() => {
    if (!foldersDialog.active) setSelectedFolder(null)
  }, [ foldersDialog.active, setSelectedFolder ])
  useEffect(() => {
    setSorted(sort(items))
  }, [ items, sort ])

  const context = {
    selectedItems,
    setSelectedItems,
    selectedFolder,
    setSelectedFolder,
    folders: selectedItemsByType.folders,
    handleFolderSelect,
    deletionDialog,
    foldersDialog,
    onMoveMediaItemsToFolder,
    onOpenContextMenu,
    onCloseContextMenu,
    clickedMediaElemId,
    setClickedMediaElemId,
    menuPositionCoords: menuPositionCoords.current,
    errorTitle,
    errorMessage,
    singleDeletion,
    forceDeletionFolderDialog,
    hotkeyDeletionDialog,
    isOpenDialog,
    isOpenContextMenu,
  }

  return (
    <BaseAssetsListContext.Provider value={context}>
      <BaseAssetsList {...rest} items={sorted} loading={loadingShow} />
    </BaseAssetsListContext.Provider>
  )
}

AssetsListContainer.propTypes = {
  type: PT.SourceFileType.isRequired,
  sort: PropTypes.func,
}

AssetsListContainer.defaultProps = {
  sort: items => items,
}

// ---

export default AssetsListContainer
